import PlusIcon from "@rsuite/icons/Plus";
import { useContext, useEffect, useRef, useState, type FC } from "react";
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  IconButton,
  Pagination,
  Table,
} from "rsuite";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import { Product } from "../models/inventory/product";
import { PaginationResponse } from "../objects/pagination";
import { BsCart3, BsEye, BsTrash3 } from "react-icons/bs";
import { errorToast, successToast } from "../utils/helper-ui";
import {
  createProduct,
  deleteProduct,
  getProducts,
  getProductsPending,
  importProduct,
  updateProduct,
} from "../services/inventory/ProductApi";
import { getPagination, money } from "../utils/helper";
import ProductForm from "../components/features/inventory/ProductForm";
import { useNavigate } from "react-router-dom";
import { ValueType } from "rsuite/esm/Checkbox";
import TagIcon from "@rsuite/icons/Tag";
import { FileModel } from "../models/file/file";
import { DiscountType } from "../models/inventory/discount";
import FileDownloadIcon from "@rsuite/icons/FileDownload";

const { Column, HeaderCell, Cell } = Table;
interface ProductPendingPageProps {}

const ProductPendingPage: FC<ProductPendingPageProps> = ({}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [products, setProducts] = useState<Product[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const nav = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [uploading, setUploading] = useState(false);

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === products.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < products.length) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: ValueType | undefined,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? products.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: ValueType | undefined,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getProductsPending({ page, size, search });

      const resJson = await res.json();
      //   console.log(data);
      setProducts((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold"> Product Pending</h1>
      {/* button */}
    </div>
  );

  const renderInputFile = () => (
    <input
      type="file"
      accept=".xls,.xlsx"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setIsLoading(true);
          setUploading(true);
          console.log(val.target.files![0]);
          await importProduct(val.target.files![0]);
          successToast(
            "Product import is running in the background, please check product list later to see the changes,"
          );
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setUploading(false);
          setIsLoading(false);
        }
      }}
    />
  );

  const removeProduct = async (product: Product) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete product with name ${product.name}?`
        )
      ) {
        await deleteProduct(product.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProduct = async (product: Product) => {
    try {
      setIsLoading(true);
      if (selectedProduct) {
        await updateProduct(selectedProduct.id!, product);
      } else {
        await createProduct(product);
      }
      setModalFormOpen(false);
      setSelectedProduct(undefined);
      fetchData();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50" style={{ width: 100 }}>
                <div className="flex items-center">
                  <Checkbox
                    inline
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={handleCheckAll}
                  />
                </div>
              </th>
             
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Name
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Images
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Description
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                SKU
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Barcode
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Price
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Brand
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Category
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Total Stock
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Discount
              </th>
              <th scope="col" className="px-4 py-2 text-left border bg-gray-50">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, i) => (
              <tr key={product.id} className="hover:bg-gray-100">
                <td className="px-4 py-2 text-left border ">
                  <Checkbox
                    value={product.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((item) => item === product.id)}
                  />
                  {(page - 1) * size + i + 1}
                </td>
             
                <td className="px-4 py-2 text-left border ">
                  <div
                    className="hover:underline cursor-pointer font-semibold "
                    onClick={() => nav(`/product/${product.id}`)}
                  >
                    {product.name}
                  </div>
                </td>
                <td className="px-4 py-2 text-left border ">
                  <div className="flex flex-wrap">
                    <AvatarGroup stack>
                      {(product?.product_images ?? [])
                        .filter((p, i) => i < 5)
                        .map((p) => (
                          <Avatar
                            size="xs"
                            bordered
                            circle
                            key={p.id}
                            src={p.url}
                            alt={p.file_name}
                          />
                        ))}
                      {(product?.product_images ?? []).length - 5 > 0 && (
                        <Avatar
                          size="xs"
                          bordered
                          circle
                          style={{ background: "#111" }}
                        >
                          +{(product?.product_images ?? []).length - 5}
                        </Avatar>
                      )}
                    </AvatarGroup>
                  </div>
                </td>
                <td className="px-4 py-2 text-left border ">
                  {product.description}
                </td>
                <td className="px-4 py-2 text-left border ">{product.sku}</td>
                <td className="px-4 py-2 text-left border ">
                  {product.barcode}
                </td>
                <td className="px-4 py-2 text-left border ">
                  <div>{money(product.price)}</div>
                </td>
                <td className="px-4 py-2 text-left border ">
                  {product.brand ? product.brand.name : ""}
                </td>
                <td className="px-4 py-2 text-left border ">
                  {product.category ? product.category.name : ""}
                </td>
                <td className="px-4 py-2 text-left border ">
                  <div>{money(product.total_stock)}</div>
                </td>
                <td className="px-4 py-2 text-left border ">
                  <div>
                    {product.active_discount
                      ? `${money(product.active_discount?.value)}${
                          product.active_discount?.type ==
                          DiscountType.PERCENTAGE
                            ? "%"
                            : ""
                        }`
                      : ""}
                  </div>
                </td>
                <td className="px-4 py-2 text-left border ">
                  <div className="flex flex-row gap-2">
                    <BsTrash3
                      className="text-red-500 hover:text-red-800 cursor-pointer"
                      size={16}
                      onClick={() => removeProduct(product)}
                    />
                    <BsEye
                      className="text-green-500 hover:text-green-800 cursor-pointer"
                      size={16}
                      onClick={() => nav(`/product/${product.id}`)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <Table minHeight={700} data={products} onRowClick={(data, i) => {}}>
        <Column width={50} align="center" fixed>
          <HeaderCell style={{ padding: 0 }}>
            <div style={{ lineHeight: "40px" }}>
              <Checkbox
                inline
                checked={checked}
                indeterminate={indeterminate}
                onChange={handleCheckAll}
              />
            </div>
          </HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(rowData) => (
              <div style={{ lineHeight: "46px" }}>
                <Checkbox
                  value={rowData.id}
                  inline
                  onChange={handleCheck}
                  checked={checkedKeys.some((item) => item === rowData.id)}
                />
              </div>
            )}
          </Cell>
        </Column>
        <Column width={70} align="left" fixed>
          <HeaderCell>No</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {(page - 1) * size + products.indexOf(rowData as Product) + 1}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable fixed>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(rowData) => (
              <div
                className="hover:underline cursor-pointer font-semibold "
                onClick={() => {
                  nav(`/product/${rowData.id}`);
                }}
              >
                {rowData.name}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={300} resizable>
          <HeaderCell>Images</HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="flex flex-wrap">
                <AvatarGroup stack>
                  {(rowData?.product_images ?? [])
                    .filter((p: FileModel, i: number) => i < 5)
                    .map((p: FileModel) => (
                      <Avatar size="xs"
                        bordered
                        circle
                        key={p.id}
                        src={p.url}
                        alt={p.file_name}
                      />
                    ))}
                  {(rowData?.product_images ?? []).length - 5 > 0 && (
                    <Avatar size="xs" bordered circle style={{ background: "#111" }}>
                      +{(rowData?.product_images ?? []).length - 5}
                    </Avatar>
                  )}
                </AvatarGroup>
              </div>
            )}
          </Cell>
        </Column>
        <Column width={300} resizable>
          <HeaderCell>Description</HeaderCell>
          <Cell dataKey="description" />
        </Column>

        <Column width={150} resizable>
          <HeaderCell>SKU</HeaderCell>
          <Cell dataKey="sku" />
        </Column>

        <Column width={150} resizable>
          <HeaderCell>Barcode</HeaderCell>
          <Cell dataKey="barcode" />
        </Column>

        <Column width={100} resizable>
          <HeaderCell>Price</HeaderCell>
          <Cell>{(rowData) => <div>{money(rowData.price)}</div>}</Cell>
        </Column>
        <Column width={150} resizable>
          <HeaderCell>Brand</HeaderCell>
          <Cell>
            {(rowData) => <div>{rowData.brand ? rowData.brand.name : ""}</div>}
          </Cell>
        </Column>
        <Column width={150} resizable>
          <HeaderCell>Category</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>{rowData.category ? rowData.category.name : ""}</div>
            )}
          </Cell>
        </Column>
        <Column width={100} resizable align="right">
          <HeaderCell>Total Stock</HeaderCell>
          <Cell>{(rowData) => <div>{money(rowData.total_stock)}</div>}</Cell>
        </Column>
        <Column width={100} resizable align="right">
          <HeaderCell>Discount</HeaderCell>
          <Cell>{(rowData) => 
            <div>{rowData.active_discount ? `${money(rowData.active_discount?.value)}${rowData.active_discount?.type == DiscountType.PERCENTAGE ? '%' : '' }` : ''}</div>}
            </Cell>
        </Column>
        <Column width={200} resizable>
          <HeaderCell>Action</HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="flex flex-row gap-2">
                <BsTrash3
                  className="text-red-500 hover:text-red-800 cursor-pointer"
                  size={16}
                  onClick={() => removeProduct(rowData as Product)}
                />
                <BsEye
                  className="text-green-500 hover:text-green-800 cursor-pointer"
                  size={16}
                  onClick={() => nav(`/product/${rowData.id}`)}
                />
              </div>
            )}
          </Cell>
        </Column>
      </Table> */}
      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      <ProductForm
        product={selectedProduct}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedProduct(undefined);
        }}
        onSubmit={saveProduct}
      />
      {renderInputFile()}
    </MainLayout>
  );
};
export default ProductPendingPage;
